import React, { useState, useEffect, useCallback } from 'react';
import { SparklesIcon, XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import {
  DurableOutlineNode,
  OutlineVersionRecord,
  OutlineAIOperation,
  TemplateItem,
  getErrorMessage,
  outlineWorkspaceApi,
} from '../services/api';

const formatDateTime = (timestamp: number) => {
  if (!timestamp) return '-';
  const d = new Date(timestamp * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export type ModalMode = 'manual_idea' | 'ai_optimize_idea' | 'ai_expand_catalog' | 'ai_optimize_catalog' | 'style_setting';

export interface HistoryVersion {
  id: string;
  title: string;
  content: string;
  style?: string;
  instruction?: string;
  createdAt: number;
}

interface CatalogEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: ModalMode;
  projectId: number;
  workspaceVersion: number;
  nodeId: string;
  nodeTitle: string;
  nodeIndex?: number;
  initialIdeaContent: string;
  initialCatalog: DurableOutlineNode[];
  ideaHistory: HistoryVersion[];
  catalogHistory: OutlineVersionRecord[];
  defaultInstruction?: string;
  onSaveIdea: (content: string, style?: string, instruction?: string) => Promise<void>;
  onGenerateIdea?: () => Promise<string>;
  onGenerateCatalog: (ideaContent: string, instruction: string, style: string) => Promise<DurableOutlineNode[] | null>;
  onInsertCatalog: (catalog: DurableOutlineNode[]) => void;
  onRestoreIdeaVersion: (version: HistoryVersion) => void;
  onRestoreCatalogVersion: (version: OutlineVersionRecord) => void;
  onRefreshHistory?: () => void;
}

export const CatalogEditModal: React.FC<CatalogEditModalProps> = ({
  isOpen,
  onClose,
  mode,
  projectId,
  workspaceVersion,
  nodeId,
  nodeTitle,
  nodeIndex,
  initialIdeaContent,
  initialCatalog,
  ideaHistory,
  catalogHistory,
  defaultInstruction,
  onSaveIdea,
  onGenerateIdea,
  onGenerateCatalog,
  onInsertCatalog,
  onRestoreIdeaVersion,
  onRestoreCatalogVersion,
  onRefreshHistory,
}) => {
  const [ideaContent, setIdeaContent] = useState(initialIdeaContent);
  const [catalog, setCatalog] = useState<DurableOutlineNode[]>(initialCatalog);
  const [instruction, setInstruction] = useState('');
  const [style, setStyle] = useState('general');
  const [showIdeaHistory, setShowIdeaHistory] = useState(false);
  const [showCatalogHistory, setShowCatalogHistory] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  
  // 从后端加载的模板
  const [styleTemplates, setStyleTemplates] = useState<TemplateItem[]>([]);
  const [instructionTemplates, setInstructionTemplates] = useState<TemplateItem[]>([]);

  const showCatalog = mode === 'ai_expand_catalog' || mode === 'ai_optimize_catalog';
  const showHistory = true;
  // AI 优化思路模式下，风格和 AI 指令已在独立弹窗「设置编写风格」里配置，
  // 这里不再重复显示，避免冗余和配置分裂
  const showInstruction = mode !== 'manual_idea' && mode !== 'ai_optimize_idea';

  // 格式化指令模板标签，移除"指令模板-"前缀
  const formatInstructionLabel = (label: string): string => {
    const prefix = '指令模板-';
    if (label.startsWith(prefix)) {
      return label.substring(prefix.length);
    }
    return label;
  };

  // 加载模板配置
  const loadTemplates = async () => {
    try {
      const response = await outlineWorkspaceApi.getTemplates(projectId);
      const data = response.data?.data;
      if (data) {
        setStyleTemplates(data.style || []);
        setInstructionTemplates(data.instruction || []);
        return data;
      }
      return { style: [], instruction: [] };
    } catch {
      console.error('加载模板配置失败');
      return { style: [], instruction: [] };
    }
  };

  // 加载操作配置
  const loadOperationConfig = async (templatesData?: { style: TemplateItem[]; instruction: TemplateItem[] }) => {
    if (!nodeId) return;
    
    // 使用传入的模板数据，或者使用 state 中的数据
    const styles = templatesData?.style || styleTemplates;
    const instructions = templatesData?.instruction || instructionTemplates;
    
    try {
      const response = await outlineWorkspaceApi.getOperationConfig(projectId, nodeId);
      const config = response.data?.data;
      if (config) {
        // 只有当配置值不为空时才覆盖
        const VALID_STYLES = ['general', 'professional', 'concise'] as const;
        type ValidStyle = typeof VALID_STYLES[number];
        const isValidStyle = (s: string | undefined): s is ValidStyle =>
          !!s && (VALID_STYLES as readonly string[]).includes(s);
        if (isValidStyle(config.operation_style)) {
          setStyle(config.operation_style);
        } else {
          setStyle(styles.find(t => t.is_default)?.value || styles[0]?.value || 'general');
        }
        if (config.operation_instruction) {
          setInstruction(config.operation_instruction);
        } else if (mode === 'ai_expand_catalog' && instructions.length > 0) {
          setInstruction(instructions.find(t => t.is_default)?.content || instructions.find(t => t.is_default)?.label || instructions[0]?.content || instructions[0]?.label || '');
        } else {
          setInstruction('');
        }
      } else {
        // 配置不存在时使用默认值
        const defaultStyle = styles.find(t => t.is_default)?.value || styles[0]?.value || 'general';
        setStyle(defaultStyle);
        
        // AI扩写目录模式下使用第一条指令模板作为默认值
        if (mode === 'ai_expand_catalog' && instructions.length > 0) {
          const defaultInstruction = instructions.find(t => t.is_default)?.content || instructions.find(t => t.is_default)?.label || instructions[0]?.content || instructions[0]?.label || '';
          setInstruction(defaultInstruction);
        } else {
          setInstruction('');
        }
      }
    } catch {
      // 配置不存在时使用默认值
      const defaultStyle = styles.find(t => t.is_default)?.value || styles[0]?.value || '';
      setStyle(defaultStyle);
      
      if (mode === 'ai_expand_catalog' && instructions.length > 0) {
        const defaultInstruction = instructions.find(t => t.is_default)?.content || instructions.find(t => t.is_default)?.label || instructions[0]?.content || instructions[0]?.label || '';
        setInstruction(defaultInstruction);
      } else {
        setInstruction('');
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIdeaContent(initialIdeaContent);
      // 打开弹窗时显示当前章节已有的目录（initialCatalog），
      // 生成只在点击「保存思路并生成目录」时进行，不自动触发
      setCatalog(initialCatalog);
      setMessage('');

      // 先加载模板配置，再加载操作配置（操作配置依赖模板数据）
      const initData = async () => {
        const templatesData = await loadTemplates();
        // 然后加载已保存的操作配置
        await loadOperationConfig(templatesData);
      };
      initData();
    }
  }, [isOpen, mode, initialIdeaContent, initialCatalog, defaultInstruction]);

  const handleSaveIdea = async () => {
    if (!ideaContent.trim()) {
      setMessage('请输入编写思路内容');
      return;
    }
    try {
      setSaving(true);
      setMessage('正在保存思路...');
      // 保存思路内容，并带上当前选中的编写风格和 AI 指令
      await onSaveIdea(ideaContent, style, instruction);
      setMessage('思路已保存');
      onRefreshHistory?.();
      setTimeout(() => {
        setMessage('');
        onClose();
      }, 1000);
    } catch (error) {
      setMessage(getErrorMessage(error, '保存思路失败'));
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateIdea = async () => {
    if (!onGenerateIdea) {
      setMessage('当前模式不支持生成思路');
      return;
    }
    try {
      setAiGenerating(true);
      setMessage('正在生成编写思路...');
      const generated = await onGenerateIdea();
      if (generated && generated.trim()) {
        setIdeaContent(generated);
        setMessage('编写思路已生成，请查看并保存');
        onRefreshHistory?.();
      } else {
        setMessage('生成结果为空');
      }
    } catch (error) {
      setMessage(getErrorMessage(error, '生成编写思路失败'));
    } finally {
      setAiGenerating(false);
    }
  };

  const handleGenerateCatalog = async () => {
    try {
      setAiGenerating(true);
      setMessage('正在生成目录...');
      const result = await onGenerateCatalog(ideaContent, instruction, style);
      if (result && Array.isArray(result)) {
        setCatalog(result);
        setMessage('目录生成成功，请查看并确认插入');
        onRefreshHistory?.();
      } else {
        setMessage('目录生成超时，请稍后在「目录历史版本」中查看，或重试');
      }
    } catch (error) {
      setMessage(getErrorMessage(error, '生成目录失败'));
    } finally {
      setAiGenerating(false);
    }
  };

  const handleInsertCatalog = async () => {
    if (catalog.length === 0) {
      setMessage('目录为空，无法插入');
      return;
    }
    setAiGenerating(true);
    setMessage('正在插入目录...');
    try {
      // 必须 await，否则弹窗会先关闭，导致后续 PUT 失败
      await onInsertCatalog(catalog);
      setMessage('目录已插入');
      setTimeout(() => {
        onClose();
      }, 600);
    } catch (error) {
      setMessage(getErrorMessage(error, '插入目录失败'));
    } finally {
      setAiGenerating(false);
    }
  };

  const handleRestoreIdeaVersion = (version: HistoryVersion) => {
    setIdeaContent(version.content);
    if (version.style) setStyle(version.style);
    if (version.instruction) setInstruction(version.instruction);
    onRestoreIdeaVersion(version);
    setMessage(`已恢复到版本：${version.title}`);
  };

  const handleRestoreCatalogVersion = async (version: OutlineVersionRecord) => {
    try {
      // 只读预览：调 snapshot 接口拿版本快照，本地 set，不写 DB
      const response = await outlineWorkspaceApi.versionSnapshot(projectId, version.version_no);
      const snapshot = response.data?.data?.snapshot;
      const outline: any[] = snapshot?.outline || [];

      // 从快照里找到当前章节节点
      const findNode = (nodes: any[], targetId: string): any => {
        for (const n of nodes) {
          if (n.id === targetId) return n;
          if (n.children) {
            const found = findNode(n.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };

      const targetNode = nodeId ? findNode(outline, nodeId) : null;

      // 1. 更新目录预览（右边）
      setCatalog((targetNode?.children as DurableOutlineNode[]) || outline);

      // 2. 更新思路文本 / 风格 / AI 指令（左边）
      const nodeForMeta = targetNode || outline[0];
      if (nodeForMeta) {
        if (nodeForMeta.merged_writing_idea) {
          setIdeaContent(nodeForMeta.merged_writing_idea);
        }
        if (nodeForMeta.operation_style) {
          setStyle(nodeForMeta.operation_style);
        }
        if (nodeForMeta.operation_instruction) {
          setInstruction(nodeForMeta.operation_instruction);
        }
      }

      setMessage(`已切到版本 V${version.version_no}，点击「插入目录」才会替换当前目录`);
    } catch (error) {
      setMessage(getErrorMessage(error, '加载版本快照失败'));
    }
  };

  if (!isOpen) return null;

  const titleMap: Record<ModalMode, string> = {
    manual_idea: '编辑编写思路',
    ai_optimize_idea: 'AI优化编写思路',
    ai_expand_catalog: 'AI扩写目录',
    ai_optimize_catalog: 'AI优化目录',
    style_setting: '设置编写风格',
  };

  // 设置编写风格（与手动编辑/AI优化思路平级的独立弹窗）
  if (mode === 'style_setting') {
    const handleSaveStyleSetting = async () => {
      try {
        setSaving(true);
        await outlineWorkspaceApi.saveOperationConfig(projectId, {
          outline_id: nodeId,
          operation_style: style,
          operation_instruction: instruction,
        });
        setMessage('编写风格已保存');
        setTimeout(() => onClose(), 600);
      } catch (error) {
        setMessage(getErrorMessage(error, '保存编写风格失败'));
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="flex h-[70vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl">
          {/* 头部 */}
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-slate-800">设置编写风格</h2>
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{nodeTitle}</span>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* 主体 */}
          <div className="flex-1 overflow-auto p-6">
            {/* 编写风格 */}
            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-slate-700">编写风格</label>
              <div className="flex flex-wrap gap-2">
                {styleTemplates.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setStyle(opt.value)}
                    className={`rounded px-3 py-1 text-xs ${
                      style === opt.value
                        ? 'bg-blue-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* AI 指令 */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <SparklesIcon className="h-4 w-4 text-amber-500" />
                <label className="text-sm font-medium text-slate-700">AI指令</label>
              </div>
              <div className="mb-3">
                <label className="mb-1 block text-xs text-slate-500">指令模板</label>
                <div className="flex flex-wrap gap-1.5">
                  {instructionTemplates.map((tpl) => {
                    const tplValue = tpl.content || tpl.label;
                    return (
                      <button
                        key={tpl.value}
                        onClick={() => setInstruction(tplValue)}
                        title={tpl.label}
                        className={`rounded px-2.5 py-1 text-xs whitespace-nowrap ${
                          instruction === tplValue
                            ? 'bg-amber-500 text-white'
                            : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                        }`}
                      >
                        {formatInstructionLabel(tpl.label)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                className="h-24 w-full resize-none rounded-lg border border-slate-300 p-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="或自定义指令内容..."
              />
            </div>
          </div>

          {/* 底部 */}
          <div className="flex items-center justify-between border-t border-slate-200 px-6 py-3">
            <div className="text-xs text-slate-500">
              {message && (
                <span className={message.includes('失败') ? 'text-red-500' : 'text-green-500'}>
                  {message}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                取消
              </button>
              <button
                onClick={handleSaveStyleSetting}
                disabled={saving}
                className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50"
              >
                {saving ? '保存中...' : '确认保存'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex h-[85vh] w-full max-w-6xl flex-col rounded-xl bg-white shadow-2xl">
        {/* 头部 */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-800">
              {titleMap[mode]}
            </h2>
            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
              {nodeTitle}
            </span>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* 主体内容 */}
        <div className="flex flex-1 overflow-hidden">
          {/* 左侧：思路编辑区 */}
          <div className={`flex flex-col border-r border-slate-200 ${showCatalog ? 'w-1/2' : 'w-full'}`}>
            {/* 历史版本入口 */}
            {showHistory && (
              <div className="border-b border-slate-100 px-4 py-2">
                <button
                  onClick={() => setShowIdeaHistory(!showIdeaHistory)}
                  className="flex w-full items-center justify-between text-sm text-slate-600 hover:text-slate-800"
                >
                  <span className="flex items-center gap-1">
                    <ClockIcon className="h-4 w-4" />
                    思路历史版本
                  </span>
                  <span className="text-xs text-slate-400">
                    {ideaHistory.length > 0 ? `${ideaHistory.length} 个版本` : '无历史'}
                  </span>
                </button>
                {showIdeaHistory && ideaHistory.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto rounded bg-slate-50 p-2">
                    {ideaHistory.map((version) => (
                      <button
                        key={version.id}
                        onClick={() => handleRestoreIdeaVersion(version)}
                        className="mb-1 w-full rounded px-2 py-1 text-left text-xs hover:bg-white"
                      >
                        <div className="font-medium text-slate-700">{version.title}</div>
                        <div className="text-slate-500">
                          {formatDateTime(version.createdAt)}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 思路编辑框 */}
            <div className="flex min-h-0 flex-1 flex-col p-4">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                编写思路
              </label>
              <textarea
                value={ideaContent}
                onChange={(e) => setIdeaContent(e.target.value)}
                className="mb-3 w-full min-h-0 flex-1 resize-none rounded-lg border border-slate-300 p-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="请输入编写思路内容..."
              />

              {/* 风格选择 - 仅 AI 功能模式显示（manual_idea 用不到 style） */}
              {!showInstruction ? null : (
                <div>
                  <label className="mb-1 block text-xs text-slate-500">编写风格</label>
                  <div className="flex flex-wrap gap-2">
                    {styleTemplates.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setStyle(opt.value)}
                        className={`rounded px-3 py-1 text-xs ${
                          style === opt.value
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* AI指令区（仅AI功能模式） */}
            {showInstruction && (
              <div className="border-t border-slate-100 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <SparklesIcon className="h-4 w-4 text-amber-500" />
                  <label className="text-sm font-medium text-slate-700">AI指令</label>
                </div>
                
                {/* AI指令模板选择（图2） - 从后端加载 */}
                <div className="mb-2">
                  <label className="mb-1 block text-xs text-slate-500">指令模板</label>
                  <div className="flex flex-wrap gap-1.5">
                    {instructionTemplates.map((tpl) => {
                      const tplValue = tpl.content || tpl.label;
                      return (
                        <button
                          key={tpl.value}
                          onClick={() => setInstruction(tplValue)}
                          title={tpl.label}
                          className={`rounded px-2.5 py-1 text-xs whitespace-nowrap ${
                            instruction === tplValue
                              ? 'bg-amber-500 text-white'
                              : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                          }`}
                        >
                          {formatInstructionLabel(tpl.label)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <textarea
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  className="mb-2 h-16 w-full resize-none rounded-lg border border-slate-300 p-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="或自定义指令内容..."
                />
              </div>
            )}

            {/* 底部按钮区 */}
            <div className="border-t border-slate-200 px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  {message && (
                    <span className={message.includes('失败') ? 'text-red-500' : 'text-green-500'}>
                      {message}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {/* 仅思路模式：AI 优化思路模式下，可一键重新生成编写思路 */}
                  {!showCatalog && mode === 'ai_optimize_idea' && onGenerateIdea && (
                    <button
                      onClick={handleGenerateIdea}
                      disabled={aiGenerating || saving}
                      className="rounded-lg border border-blue-500 bg-white px-4 py-2 text-sm font-medium text-blue-500 hover:bg-blue-50 disabled:opacity-50"
                      title="调用 AI 重新生成该章节的编写思路（不会自动保存）"
                    >
                      {aiGenerating ? '生成中...' : '生成编写思路'}
                    </button>
                  )}
                  {/* 仅思路模式：保存思路按钮 */}
                  {!showCatalog && (
                    <button
                      onClick={handleSaveIdea}
                      disabled={saving}
                      className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50"
                    >
                      {saving ? '保存中...' : '保存思路'}
                    </button>
                  )}
                  {/* 带目录模式：保存思路并生成目录 */}
                  {showCatalog && (
                    <>
                      <button
                        onClick={handleGenerateCatalog}
                        disabled={aiGenerating}
                        className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50"
                      >
                        {aiGenerating ? '生成中...' : '保存思路并生成目录'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 右侧：目录预览区 */}
          {showCatalog && (
            <div className="flex w-1/2 flex-col">
              {/* 历史版本入口 */}
              <div className="border-b border-slate-100 px-4 py-2">
                <button
                  onClick={() => setShowCatalogHistory(!showCatalogHistory)}
                  className="flex w-full items-center justify-between text-sm text-slate-600 hover:text-slate-800"
                >
                  <span className="flex items-center gap-1">
                    <ClockIcon className="h-4 w-4" />
                    目录历史版本
                  </span>
                  <span className="text-xs text-slate-400">
                    {catalogHistory.length > 0 ? `${catalogHistory.length} 个版本` : '无历史'}
                  </span>
                </button>
                {showCatalogHistory && catalogHistory.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto rounded bg-slate-50 p-2">
                    {catalogHistory.map((version) => (
                      <button
                        key={version.version_no}
                        onClick={() => handleRestoreCatalogVersion(version)}
                        className="mb-1 w-full rounded px-2 py-1 text-left text-xs hover:bg-white"
                      >
                        <div className="font-medium text-slate-700">
                          版本 {version.version_no}
                        </div>
                        <div className="text-slate-500">
                          {version.source_type} - {formatDateTime(version.create_time)}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 目录预览 */}
              <div className="flex-1 overflow-auto p-4">
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  目录预览
                </label>
                {catalog.length > 0 ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    {catalog.map((node) => renderCatalogNode(node, 0))}
                  </div>
                ) : (
                  <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-slate-300 text-sm text-slate-400">
                    暂无目录，请先生成
                  </div>
                )}
              </div>

              {/* 插入目录按钮 */}
              <div className="border-t border-slate-200 px-4 py-3">
                <button
                  onClick={handleInsertCatalog}
                  disabled={catalog.length === 0 || aiGenerating}
                  className="w-full rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
                >
                  {aiGenerating ? '插入中...' : '插入目录'}
                </button>
                <p className="mt-1 text-center text-xs text-slate-500">
                  点击后将替换当前章节的目录
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 递归渲染目录节点
const renderCatalogNode = (node: DurableOutlineNode, level: number): React.ReactNode => {
  const indent = level * 20;
  return (
    <div key={node.id} style={{ marginLeft: `${indent}px` }}>
      <div className="flex items-center gap-2 py-1">
        <span className="text-sm text-slate-700">{node.title}</span>
        {node.expected_word_count > 0 && (
          <span className="text-xs text-slate-400">({node.expected_word_count}字)</span>
        )}
      </div>
      {node.children && node.children.length > 0 && (
        <div>{node.children.map((child) => renderCatalogNode(child, level + 1))}</div>
      )}
    </div>
  );
};